import React, { useCallback, useState, useEffect } from 'react'
import { RecaptchaV3Provider } from '../providers/RecaptchaV3Provider'
import { useCaptcha } from '../hooks/useCaptcha'
import { getCaptchaConfig } from '../services/captchaService'
import { WithCaptchaHOC, CaptchaFormData } from '../types'

/**
 * withCaptcha HOC (Higher-Order Component)
 * Wraps existing forms to add CAPTCHA functionality without modifying them
 *
 * Following OCP: Extends form functionality without changing existing components
 */

export const withCaptcha: WithCaptchaHOC<any> = (WrappedComponent) => {
  const WithCaptchaComponent = React.forwardRef<any, any>((props, ref) => {
    // CAPTCHA state and methods
    const {
      captchaRef,
      executeCaptcha,
      isEnabled,
      isReady,
      error: captchaError
    } = useCaptcha({
      onVerify: props.onCaptchaVerify,
      onError: props.onCaptchaError
    })

    // Local state for CAPTCHA token and site key
    const [captchaToken, setCaptchaToken] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [siteKey, setSiteKey] = useState<string>('')

    // Default submit handler if none provided
    const defaultSubmitHandler = async (_formData: any) => {
      // This should not be called for LoginPage as it has its own submit logic
    }

    // Load site key from API configuration
    useEffect(() => {
      const loadSiteKey = async () => {
        if (isEnabled) {
          try {
            const config = await getCaptchaConfig()
            setSiteKey(config.siteKey || '')
          } catch (error) {
            console.warn('Failed to load CAPTCHA site key:', error)
          }
        }
      }
      loadSiteKey()
    }, [isEnabled])

    // Enhanced submit handler
    const handleSubmit = useCallback(async (formData: CaptchaFormData) => {
      // If CAPTCHA is disabled, submit normally
      if (!isEnabled) {
        const submitHandler = props.onSubmit || defaultSubmitHandler
        return submitHandler(formData)
      }

      // If CAPTCHA is not ready, show error
      if (!isReady) {
        const error = 'CAPTCHA is not ready. Please wait and try again.'
        props.onCaptchaError?.(error)
        return
      }

      setIsSubmitting(true)

      try {
        // Execute CAPTCHA to get token
        const token = await executeCaptcha()

        if (!token) {
          throw new Error('Failed to obtain CAPTCHA token')
        }

        // Add CAPTCHA token to form data
        const enhancedFormData = {
          ...formData,
          captchaToken: token
        }

        setCaptchaToken(token)

        // Call original submit handler with enhanced data
        const submitHandler = props.onSubmit || defaultSubmitHandler
        await submitHandler(enhancedFormData)

      } catch (_error: any) {
        // For now, allow the form to submit without CAPTCHA if there's an error
        // Call original submit handler without CAPTCHA token
        const submitHandler = props.onSubmit || defaultSubmitHandler
        await submitHandler(formData)
        
      } finally {
        setIsSubmitting(false)
      }
    }, [isEnabled, isReady, executeCaptcha, props])

    // Enhanced props for wrapped component
    const enhancedProps = {
      ...props,
      // Add CAPTCHA-related props
      captchaEnabled: isEnabled,
      captchaReady: isReady,
      captchaError,
      captchaToken,
      isSubmitting: props.isSubmitting || isSubmitting,

      // Override onSubmit to include CAPTCHA logic
      onSubmit: handleSubmit,

      // Additional CAPTCHA methods
      onCaptchaRetry: useCallback(() => {
        setCaptchaToken(null)
        // CAPTCHA will auto-reset via the hook
      }, [])
    }

    return (
      <>
        {/* CAPTCHA Provider (only render if enabled and site key is available) */}
        {isEnabled && siteKey && (
          <RecaptchaV3Provider
              ref={captchaRef}
              siteKey={siteKey}
              onVerify={(token) => {
                setCaptchaToken(token)
                props.onCaptchaVerify?.(token)
              }}
              onError={(error) => {
                props.onCaptchaError?.(error.message)
              }}
            />
        )}

        {/* Wrapped Component with enhanced props */}
        <WrappedComponent {...enhancedProps} ref={ref} />
      </>
    )
  })

  WithCaptchaComponent.displayName = `withCaptcha(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

  return WithCaptchaComponent
}

/**
 * Alternative: Conditional CAPTCHA wrapper
 * Only applies CAPTCHA based on a condition function
 */
export const withConditionalCaptcha = (
  condition: (props: any) => boolean
): WithCaptchaHOC<any> => {
  return (WrappedComponent) => {
    const ConditionalCaptchaComponent = React.forwardRef<any, any>((props, ref) => {
      // Check condition
      const shouldApplyCaptcha = condition(props)

      // Use regular HOC or return component unchanged
      if (shouldApplyCaptcha) {
        const ComponentWithCaptcha = withCaptcha(WrappedComponent)
        return <ComponentWithCaptcha {...props} ref={ref} />
      }

      return <WrappedComponent {...props} ref={ref} />
    })

    ConditionalCaptchaComponent.displayName = `withConditionalCaptcha(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

    return ConditionalCaptchaComponent
  }
}

/**
 * Utility hook for components wrapped with withCaptcha
 * Provides access to CAPTCHA state and methods
 */
export const useCaptchaInForm = () => {
  const captcha = useCaptcha()

  const getFormDataWithCaptcha = useCallback(async (formData: any) => {
    if (!captcha.isEnabled) {
      return formData
    }

    const token = await captcha.executeCaptcha()
    return {
      ...formData,
      captchaToken: token
    }
  }, [captcha])

  return {
    ...captcha,
    getFormDataWithCaptcha
  }
}
